/**
 * Component Name: MonthQuarterGroupedTable
 * Description: A table component that displays invoice data grouped by month or quarter, with sortable columns and clickable periods.
 * Props:
 * - filteredInvoices (array): List of filtered invoice objects to group and display.
 * - handlePeriodClick (function): Callback to handle clicks on period labels for drill-down.
 * - selectedGroup (string): The grouping type ('month' or 'quarter').
 * Feature: Groups invoices by month or quarter, displays aggregated data (amounts, counts), and allows users to click periods to view detailed invoices in the invoice management interface.
 */
import React, { useState, useEffect } from 'react';
import CustomTable from '../table/table';
import { toUSD } from '../../util/currency-formatter';
import { toDecimal, roundValue } from '../../util/decimal-utils';
import moment from 'moment';
import { GSTORVAT } from '../../util/enums';
import { getQuarterCalculator } from '../../routes/suppliers/utils/finance-quarter';
import { useDispatch, useSelector } from 'react-redux';
import { getGlobalSettingReducer } from '../../actions/global-setting.action';
import { getQuarterFromDate as fallbackGetQuarterFromDate } from '../../routes/suppliers/utils/finance-quarter';
import { INVOICE_CSS_CLASSES, INVOICE_GROUPING_VALUES, INVOICE_TABLE_COLUMNS, INVOICE_ERROR_MESSAGES, INVOICE_DATE_FORMAT, INVOICE_MONTH_FORMAT } from '../../util/enums/invoice-management';

const MonthQuarterGroupedTable = ({ filteredInvoices, handlePeriodClick, selectedGroup }) => {
    const dispatch = useDispatch();
    // Get the entire globalSetting state, specifically finance_tax
    const financeSettings = useSelector(state => state.globalSetting?.finance_tax || {});
    const [quarterCalculator, setQuarterCalculator] = useState(() => fallbackGetQuarterFromDate); // Initialize with fallback

    // Load global settings on component mount if not already available
    useEffect(() => {
        // Only dispatch if finance_tax is empty or not yet loaded
        if (!financeSettings || Object.keys(financeSettings).length === 0) {
            dispatch(getGlobalSettingReducer('FINANCE_TAX'));
        }
    }, [financeSettings]); // Re-run if financeSettings change

    // Update quarterCalculator when financeSettings change
    useEffect(() => {
        if (financeSettings && Object.keys(financeSettings).length > 0) {
            const calculator = getQuarterCalculator(financeSettings);
            setQuarterCalculator(() => calculator);
        } else {
            setQuarterCalculator(() => fallbackGetQuarterFromDate);
        }
    }, [financeSettings]); // Re-run when financeSettings from Redux change

    try {
        const periodMap = new Map();
        const periodPoIdSets = new Map(); // Track unique PO IDs per period

        filteredInvoices.forEach((inv) => {
            try {
                const issueDate = moment(inv.issueDate, INVOICE_DATE_FORMAT, true); // Strict parsing
                let periodKey;
                let periodLabel;

                if (inv.issueDate === 'N/A' || !issueDate.isValid()) {
                    // Assign a specific key and label for invoices with missing or invalid issue dates
                    periodKey = 'empty-issue-date';
                    periodLabel = 'Empty Issue Date';
                } else if (selectedGroup === INVOICE_GROUPING_VALUES.MONTH) {
                    periodKey = issueDate.format(INVOICE_MONTH_FORMAT);
                    periodLabel = issueDate.format('MMMM YYYY');
                } else if (selectedGroup === INVOICE_GROUPING_VALUES.QUARTER) {
                    const quarterInfo = quarterCalculator(issueDate);
                    periodKey = quarterInfo.quarterKey;
                    periodLabel = quarterInfo.quarterLabel;
                } else {
                    return; // Should not happen with proper selectedGroup
                }

                if (!periodMap.has(periodKey)) {
                    periodMap.set(periodKey, {
                        periodKey,
                        periodLabel,
                        totalInvoiceAmount: toDecimal(0),
                        totalInvoiceSubtotal: toDecimal(0),
                        invoiceCount: 0,
                        poTotal: toDecimal(0),
                        poCount: 0,
                    });
                    periodPoIdSets.set(periodKey, new Set());
                }

                const period = periodMap.get(periodKey);
                const poIdSet = periodPoIdSets.get(periodKey);
                period.totalInvoiceAmount = period.totalInvoiceAmount.plus(toDecimal(inv.totalAmount || 0));
                period.totalInvoiceSubtotal = period.totalInvoiceSubtotal.plus(toDecimal(inv.subTotal || 0));
                period.invoiceCount += 1;
                period.poTotal = period.poTotal.plus(toDecimal(inv.PurchaseOrders?.total || 0));

                const poId = inv.PurchaseOrders?.id;
                if (poId && !poIdSet.has(poId)) {
                    poIdSet.add(poId);
                    period.poCount += 1;
                }
            } catch (error) {
                console.error('Error processing invoice:', error);
            }
        });

        // Sort the data by periodKey (chronologically for months/quarters)
        const sortedGroupedData = Array.from(periodMap.values()).sort((a, b) => {
            // Place 'Empty Issue Date' at the end of the table
            if (a.periodKey === 'empty-issue-date') return 1;
            if (b.periodKey === 'empty-issue-date') return -1;

            if (selectedGroup === INVOICE_GROUPING_VALUES.MONTH) {
                return moment(a.periodKey, INVOICE_MONTH_FORMAT).valueOf() - moment(b.periodKey, INVOICE_MONTH_FORMAT).valueOf();
            } else if (selectedGroup === INVOICE_GROUPING_VALUES.QUARTER) {
                // Custom sorting for quarters (e.g., "2024-Q1" before "2024-Q2")
                return a.periodKey.localeCompare(b.periodKey);
            }
            return 0;
        }).map((value) => ({
            ...value,
            totalInvoiceAmount: roundValue(value.totalInvoiceAmount),
            totalInvoiceSubtotal: roundValue(value.totalInvoiceSubtotal),
            poTotal: roundValue(value.poTotal),
        }));

        const columns = [
            {
                title: selectedGroup === INVOICE_GROUPING_VALUES.MONTH ? INVOICE_TABLE_COLUMNS.MONTH : INVOICE_TABLE_COLUMNS.QUARTER,
                field: 'periodLabel',
                render: (row) => (
                    <span className={INVOICE_CSS_CLASSES.PERIOD_LINK} onClick={() => handlePeriodClick(row.periodKey, row.periodLabel, selectedGroup)}>
                        {row.periodLabel}
                    </span>
                ),
                sortable: true,
                columnSearch: true,
            },
            {
                title: INVOICE_TABLE_COLUMNS.TOTAL_INVOICE_AMOUNT,
                field: 'totalInvoiceAmount',
                class: 'text-right justify-content-end',
                render: (row) => <span className={INVOICE_CSS_CLASSES.AMOUNT}>{toUSD(row.totalInvoiceAmount)}</span >,
                sortable: true,
                columnSearch: true,
            },
            {
                title: `${INVOICE_TABLE_COLUMNS.SUB_TOTAL} (Ex. ${GSTORVAT})`,
                field: 'totalInvoiceSubtotal',
                class: 'text-right justify-content-end',
                render: (row) => <span className={INVOICE_CSS_CLASSES.SUB_TOTAL_AMOUNT}>{toUSD(row.totalInvoiceSubtotal)}</span>,
                sortable: true,
                columnSearch: true,
            },
            {
                title: INVOICE_TABLE_COLUMNS.INVOICE_COUNT,
                field: 'invoiceCount',
                class: 'text-right justify-content-end',
                sortable: true,
                columnSearch: true,
            },
            {
                title: INVOICE_TABLE_COLUMNS.PO_TOTAL,
                field: 'poTotal',
                class: 'text-right justify-content-end',
                render: (row) => <span>{toUSD(row.poTotal)}</span>,
                sortable: true,
                columnSearch: true,
            },
            {
                title: INVOICE_TABLE_COLUMNS.PO_COUNT,
                field: 'poCount',
                class: 'text-right justify-content-end',
                render: (row) => <span>{row.poCount}</span>,
                sortable: true,
                columnSearch: true,
            },
        ];
        const tableItemClickHandler = (row) => {
            handlePeriodClick(row.periodKey, row.periodLabel, selectedGroup);
        };

        return (
            <div className="table-container">
                <CustomTable columns={columns} data={sortedGroupedData} pagination={true} rowClickHandler={tableItemClickHandler} />
            </div>
        );
    } catch (error) {
        console.error('Error in MonthQuarterGroupedTable:', error);
        return (
            <div className="alert alert-danger">
                <strong>Error:</strong> {INVOICE_ERROR_MESSAGES.LOAD_TABLE_ERROR}
            </div>
        );
    }
};

export default MonthQuarterGroupedTable;